/**
 * fav-toggle.js — 共用「收藏」切換模組（前台 prototype，純前端，不依賴後台）
 *
 * 站上唯一收藏 toggle 行為的單一真相。原寫於 article-detail.js 的 initFavorite，
 * 抽出參數化供 article-detail / clinic-detail（及未來其他項目）真共用同一套狀態機，
 * 避免複製貼上各自漂移。
 *
 * 行為（與原 article 版一致）：
 *   未收藏(rest)  ：空心 icon / restLabel / restClass 按鈕色 / aria-pressed=false
 *   已收藏(active)：實心 icon（加 fillClass）/ activeLabel / activeClass 按鈕色 / aria-pressed=true
 *   false→true（新收藏）：icon 彈一下（加 popClass，true→false 不彈）
 *   狀態存 localStorage（storageKey 對應一個 id array），載入還原，try/catch fail-safe。
 *
 * 按鈕配色用「rest/active class 對切」抽象（非寫死 primary/secondary）：
 *   各頁依自身視覺層級傳入 restClass / activeClass。article 用 primary→secondary，
 *   clinic 用 secondary→added（保留收藏為次級 CTA，不與主 CTA「預約諮詢」同重）。
 *   兩頁共用同一組狀態語彙（填滿 / 變色 / 文字 / aria / 首次彈跳），只配色層級不同。
 *
 * @param {Object} config
 * @param {HTMLElement} [config.btn]       直接傳入按鈕元素（優先於 selector）
 * @param {string}      [config.selector]  按鈕 CSS selector（btn 未傳時用）
 * @param {string}      config.id          收藏項目 id（存進 storage array 的值）
 * @param {string}      config.storageKey  localStorage key
 * @param {string}      config.restLabel   未收藏文字
 * @param {string}      config.activeLabel 已收藏文字
 * @param {string}      [config.iconSelector]  icon 元素 selector（相對按鈕）
 * @param {string}      [config.labelSelector='[data-fav-label]'] 文字 span selector（相對按鈕）
 * @param {string}      [config.restClass]   rest 態按鈕 class（active 時移除）
 * @param {string}      [config.activeClass] active 態按鈕 class（rest 時移除）
 * @param {string}      [config.fillClass='is-filled']   icon 填滿 class
 * @param {string}      [config.popClass='is-popping']   首次收藏彈跳 class
 * @param {Function[]}  cleanups           listener 解綁清單（呼叫端在 pagehide/HMR 統一解除）
 * @returns {boolean} 是否成功初始化（找不到按鈕 / 缺 id 時回 false）
 */
// TODO(backend): 收藏改打 GET/POST/DELETE favorites API 持久化（取代 localStorage），列表撈實際資料 — 見 docs/BACKEND_HANDOFF.md#member-favorites
export function initFavToggle(config, cleanups) {
  const {
    btn: btnArg,
    selector,
    id,
    storageKey,
    restLabel,
    activeLabel,
    iconSelector,
    labelSelector = '[data-fav-label]',
    restClass,
    activeClass,
    fillClass = 'is-filled',
    popClass = 'is-popping',
  } = config;

  const btn = btnArg || (selector ? document.querySelector(selector) : null);
  if (!btn) return false;
  if (!id) return false;

  const icon = iconSelector ? btn.querySelector(iconSelector) : null;
  const label = btn.querySelector(labelSelector);

  /** 讀收藏清單（id array）。localStorage 損毀 / 不可用時回空陣列（fail-safe）。 */
  const readFavorites = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeFavorites = (list) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(list));
    } catch {
      /* 隱私模式 / 配額滿：靜默忽略，不阻斷互動 */
    }
  };

  /** 套用視覺狀態（不寫 storage、不彈跳）。pop 由 click 路徑單獨控制。 */
  const applyState = (filled) => {
    btn.setAttribute('aria-pressed', filled ? 'true' : 'false');
    if (restClass) btn.classList.toggle(restClass, !filled);
    if (activeClass) btn.classList.toggle(activeClass, filled);
    if (icon) icon.classList.toggle(fillClass, filled);
    if (label) label.textContent = filled ? activeLabel : restLabel;
  };

  // 還原：載入時若此項已在收藏清單 → 已收藏態（不彈）
  applyState(readFavorites().includes(id));

  const onClick = () => {
    const list = readFavorites();
    const wasFav = list.includes(id);
    const next = wasFav ? list.filter((x) => x !== id) : [...list, id];
    writeFavorites(next);
    applyState(!wasFav);

    // 僅 false→true（新收藏）彈跳；用 animationend 自清 class 以便可重複觸發
    if (!wasFav && icon) {
      icon.classList.remove(popClass);
      // 強制 reflow 重啟動畫（連續收藏/取消/再收藏時 class 才會重觸發）
      void icon.offsetWidth;
      icon.classList.add(popClass);
    }
  };

  const onAnimEnd = () => icon && icon.classList.remove(popClass);

  btn.addEventListener('click', onClick);
  if (icon) icon.addEventListener('animationend', onAnimEnd);
  if (cleanups) {
    cleanups.push(() => {
      btn.removeEventListener('click', onClick);
      if (icon) icon.removeEventListener('animationend', onAnimEnd);
    });
  }

  return true;
}
